import './Delivery.scss';
import { abbreviateNumber } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { getMetricFormatter } from '@blisspointmedia/bpm-types/dist/metricFormatUtils';
import { DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import cn from 'classnames';
import { Dispatch, JSX, SetStateAction, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CellData, DimensionMap } from '@Components/MetricsTable/metrics-table-utils';
import { PercentChange } from '@Components/PercentChange/PercentChange';
import { FullPageSpinner } from '@Components/Spinner';
import { Size, StretchMode } from '@Components/designConstants';
import { FilterPaneState } from '@blisspointmedia/bpm-types/dist/FilterPane';
import { DateRange } from '@src/utils/date-utils';
import { SingleChannelMediaTypes } from '@src/utils/performance-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useContainerSize } from '@src/utils/hooks/useContainerSize';
import { withRatioAggregators } from '@src/utils/metrics/ratioAggregation';
import { InvalidGranularityDetails } from '@src/utils/columnMetricSystem/configUtils';
import { DailyDeliveryData } from './delivery-fetch';
import {
  getCompanyChannelColumnMetaData,
  sharedColumnMetaDataMap
} from '../Channels/shared-performance-utils';
import {
  SingleChannelDeliveryChart,
  DEFAULT_DELIVERY_METRICS_OPTIONS,
  DeliveryMetricOption
} from './SingleChannelDeliveryChart';

const OVERVIEW_KEYS: [string, string, string] = ['spend', 'impressions', 'cpm'];

// "$10,325,737" -> "$10.33M", "2,089,849,748" -> "2.09B". Anything that isn't a plain (prefixed)
// number, or is under 1,000, is returned as formatted.
const abbreviateFormattedValue = (formatted: string, rawValue: number): string => {
  const match = formatted.match(/^([^\d-]*)[\d,.]+$/);
  if (!match || Math.abs(rawValue) < 1000) {
    return formatted;
  }
  return `${match[1]}${abbreviateNumber(rawValue)}`;
};

interface DeliveryOverviewItemProps {
  name: string;
  percentChange: number;
  rawValue: number;
  showTotal: boolean;
  value: string;
}

// Space-saving steps, applied in order until the card's content fits. The metric name is never
// shortened.
const FitLevel = {
  FULL: 0,
  ABBREVIATED_VALUE: 1,
  ABBREVIATED_VALUE_NO_TOTAL: 2,
  TIGHT: 3
} as const;

// Shows the full value while it fits the card; if not, the abbreviated one (full value in a
// tooltip); if that still doesn't fit, it also drops the "Total" label; as a last step it tightens
// the card's padding and type size.
const DeliveryOverviewItem = ({
  name,
  percentChange,
  rawValue,
  showTotal,
  value
}: DeliveryOverviewItemProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const width = useContainerSize(ref, { widthOnly: true })?.width;
  const [fitLevel, setFitLevel] = useState<number>(FitLevel.FULL);

  // Whenever the card's width or the value changes, start over from the full value and re-test.
  // Both run in layout effects, so the steps land before paint.
  useLayoutEffect(() => {
    setFitLevel(FitLevel.FULL);
  }, [width, value]);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && el.scrollWidth > el.clientWidth && fitLevel < FitLevel.TIGHT) {
      setFitLevel(fitLevel + 1);
    }
  }, [fitLevel, width, value]);

  const abbreviated = fitLevel >= FitLevel.ABBREVIATED_VALUE;
  return (
    <div className={cn('delivery-overview-item', { tight: fitLevel === FitLevel.TIGHT })} ref={ref}>
      <div className='overview-value-key-container'>
        <div className='overview-value' title={abbreviated ? value : undefined}>
          {abbreviated ? abbreviateFormattedValue(value, rawValue) : value}
        </div>
        <div className='overview-key'>{name}</div>
      </div>
      {showTotal && fitLevel < FitLevel.ABBREVIATED_VALUE_NO_TOTAL && (
        <div className='overview-type'>Total</div>
      )}
      <div className='percent-change-container'>
        <PercentChange percentChange={percentChange} size={Size.MD} stretchMode={StretchMode.FIT} />
      </div>
    </div>
  );
};

interface DeliveryProps {
  company: string;
  data: DailyDeliveryData;
  dates: DateRange;
  defaultDateGroupingKey?: DateGroupingKey;
  defaultMetric?: string;
  deliveryMetricOptions?: DeliveryMetricOption[];
  dimension: string;
  // prettyName -> snake_case name for each delivery dimension
  deliveryDimensionMap: Record<string, string>;
  getDimensionCell: (
    dimensionData: DimensionMap,
    dimensionHeader: DimensionColumn,
    ...args: unknown[]
  ) => CellData;
  otherData: DailyDeliveryData;
  otherDates?: DateRange;
  prefix: SingleChannelMediaTypes;
  setDimension: Dispatch<SetStateAction<string>>;
  overviewKeys?: [string, string, string];
  incompatibleDeliveryDimsFromFilter?: InvalidGranularityDetails;
  filterID?: number;
  filterState?: FilterPaneState;
}

// Sums the delivery metrics for every dimension across every date in the (daily, falling back to
// weekly when daily is empty) data set. Shared by the primary and comparison date ranges below.
const sumDeliveryMetrics = (
  data: DailyDeliveryData | undefined,
  deliveryMetricOptions: DeliveryMetricOption[]
): Record<string, number> => {
  const totals: Record<string, number> = {};
  for (const option of deliveryMetricOptions) {
    totals[option.value] = 0;
  }

  const primaryData =
    data?.daily && Object.keys(data.daily).length === 0 && data.weekly ? data.weekly : data?.daily;
  if (!primaryData) {
    return totals;
  }

  for (const date of Object.keys(primaryData)) {
    const dateRow = primaryData[date];
    if (!dateRow) continue;
    for (const dimension of Object.keys(dateRow)) {
      const dimensionRow = dateRow[dimension];
      if (!dimensionRow) continue;
      for (const option of deliveryMetricOptions) {
        totals[option.value] = (totals[option.value] || 0) + (dimensionRow[option.value] || 0);
      }
    }
  }
  return totals;
};

export const Delivery = ({
  company,
  data,
  dates,
  defaultDateGroupingKey,
  defaultMetric,
  deliveryMetricOptions = DEFAULT_DELIVERY_METRICS_OPTIONS,
  dimension,
  deliveryDimensionMap,
  getDimensionCell,
  otherData,
  otherDates,
  prefix,
  setDimension,
  overviewKeys = OVERVIEW_KEYS,
  incompatibleDeliveryDimsFromFilter = {},
  filterID,
  filterState
}: DeliveryProps): JSX.Element => {
  const { currency_code: currencyCode } = useCompanyInfo();
  const metricFormatter = useMemo(() => getMetricFormatter(currencyCode), [currencyCode]);
  const columnMetaData = useMemo(
    () =>
      withRatioAggregators(getCompanyChannelColumnMetaData(sharedColumnMetaDataMap, currencyCode)),
    [currencyCode]
  );

  const overview = useMemo(() => {
    if (!data || (!otherData && otherDates)) {
      return undefined;
    }

    const overviewData: Record<string, { primary: number; comparison: number }> = {};
    const primaryTotals = sumDeliveryMetrics(data, deliveryMetricOptions);
    const comparisonTotals = sumDeliveryMetrics(otherData, deliveryMetricOptions);
    for (const key of Object.keys(primaryTotals)) {
      overviewData[key] = { primary: primaryTotals[key], comparison: comparisonTotals[key] || 0 };
    }

    for (const metric of overviewKeys) {
      const metaData = columnMetaData[metric];
      if (metaData?.requiredColumns && metaData.aggregator) {
        const { aggregator, requiredColumns } = metaData;
        const primaryDataObj: Record<string, number> = {};
        const comparisonDataObj: Record<string, number> = {};
        for (const col of requiredColumns) {
          primaryDataObj[col] = overviewData[col] ? overviewData[col].primary : 0;
          comparisonDataObj[col] = overviewData[col] ? overviewData[col].comparison : 0;
        }
        const primaryVal = aggregator(primaryDataObj);
        const compVal = aggregator(comparisonDataObj);
        overviewData[metric] = {
          primary: typeof primaryVal === 'number' ? primaryVal : 0,
          comparison: typeof compVal === 'number' ? compVal : 0
        };
      }
    }

    return (
      <div className='delivery-overview'>
        {overviewKeys.map(key => {
          const percentChange =
            overviewData[key].primary && overviewData[key].comparison
              ? ((overviewData[key].primary - overviewData[key].comparison) /
                  overviewData[key].comparison) *
                100
              : 0;
          const value = metricFormatter(key)
            ? metricFormatter(key)(overviewData[key].primary)
            : metricFormatter('number')(overviewData[key].primary);

          const isSummedTotal = columnMetaData[key] && !columnMetaData[key]?.requiredColumns;
          const nameToDisplay =
            key === 'impressions' ? 'Impressions' : columnMetaData[key]?.prettyName || key;
          return (
            <DeliveryOverviewItem
              key={`delivery-overview-item-${key}`}
              name={nameToDisplay}
              percentChange={percentChange}
              rawValue={overviewData[key].primary}
              showTotal={Boolean(isSummedTotal)}
              value={value}
            />
          );
        })}
      </div>
    );
  }, [
    data,
    deliveryMetricOptions,
    otherData,
    otherDates,
    metricFormatter,
    overviewKeys,
    columnMetaData
  ]);

  if (!overview || !data || (!otherData && otherDates)) {
    return <FullPageSpinner />;
  }

  return (
    <div className='delivery-tab'>
      {overview}
      <SingleChannelDeliveryChart
        company={company}
        data={data}
        dates={dates}
        defaultDateGroupingKey={defaultDateGroupingKey}
        defaultMetric={defaultMetric}
        deliveryMetricOptions={deliveryMetricOptions}
        dimension={dimension}
        deliveryDimensionMap={deliveryDimensionMap}
        getDimensionCell={getDimensionCell}
        otherData={otherData}
        otherDates={otherDates}
        prefix={prefix}
        setDimension={setDimension}
        efficiencyMetric={overviewKeys[2]}
        incompatibleDeliveryDimsFromFilter={incompatibleDeliveryDimsFromFilter}
        filterID={filterID}
        filterState={filterState}
      />
    </div>
  );
};
